import React, { useState, useEffect } from 'react';
import { 
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography
} from '@mui/material';
import { 
  WifiOff as OfflineIcon,
  Wifi as OnlineIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';

/**
 * Composant MenuItem qui affiche le statut de connexion dans le menu déroulant du profil
 */
const ConnectionStatusMenuItem = ({ onMenuClose }) => {
  const { currentUser } = useAuth();
  const { addSystemNotification } = useNotifications();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [authWarning, setAuthWarning] = useState(false);

  // Surveiller le statut de connexion réseau
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      
      // Afficher une notification de reconnexion
      addSystemNotification('Connexion rétablie', 'success');
      
      // Masquer l'avertissement d'authentification s'il était affiché
      setAuthWarning(false);
    };

    const handleOffline = () => {
      setIsOnline(false);

      // Afficher une notification de perte de connexion
      addSystemNotification('Connexion interrompue - Fonctionnement en mode hors ligne', 'error');
    };

    // Ajouter les écouteurs d'événements
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Nettoyer les écouteurs
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [addSystemNotification]);

  // Surveiller les événements d'authentification personnalisés
  useEffect(() => {
    const handleTokenRefresh = () => {
      // Masquer l'avertissement d'authentification
      setAuthWarning(false);
    };

    const handleTokenExpired = () => {
      addSystemNotification('Votre session a expiré. Veuillez vous reconnecter.', 'error');
      setAuthWarning(true);
    };

    const handleAuthRefreshStart = (event) => {
      const message = event.detail?.message || 'Actualisation de votre session...';
      addSystemNotification(message, 'success');
    };

    const handleAuthRefreshEnd = () => {
      // Les notifications de chargement sont automatiquement gérées par le système
      // Pas besoin de les fermer manuellement
    };

    // Ajouter les écouteurs d'événements personnalisés
    window.addEventListener('firebase-token-refreshed', handleTokenRefresh);
    window.addEventListener('azure-token-expired', handleTokenExpired);
    window.addEventListener('auth-refresh-start', handleAuthRefreshStart);
    window.addEventListener('auth-refresh-end', handleAuthRefreshEnd);

    // Nettoyer les écouteurs
    return () => {
      window.removeEventListener('firebase-token-refreshed', handleTokenRefresh);
      window.removeEventListener('azure-token-expired', handleTokenExpired);
      window.removeEventListener('auth-refresh-start', handleAuthRefreshStart);
      window.removeEventListener('auth-refresh-end', handleAuthRefreshEnd);
    };
  }, [addSystemNotification]);

  // Déterminer l'icône et le texte du statut
  const getStatusIcon = () => {
    if (!isOnline) return <OfflineIcon color="error" />;
    if (authWarning) return <WarningIcon color="warning" />;
    return <OnlineIcon color="success" />;
  };

  const getStatusText = () => {
    if (!isOnline) return 'Hors ligne';
    if (authWarning) return 'Problème d\'authentification';
    return 'En ligne';
  };

  const getStatusDescription = () => {
    if (!isOnline) {
      return 'Connexion réseau indisponible. Certaines fonctionnalités peuvent être limitées.';
    }
    if (authWarning) {
      return 'Votre session a expiré ou rencontre des problèmes. Reconnectez-vous pour résoudre le problème.';
    }
    return 'Connexion active et authentification fonctionnelle.';
  };

  return (
    <MenuItem disabled sx={{ py: 1.5 }}>
      <ListItemIcon>
        {getStatusIcon()}
      </ListItemIcon>
      <ListItemText 
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight="medium">
              {getStatusText()}
            </Typography>
          </Box>
        }
        secondary={
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ 
              display: 'block',
              mt: 0.5,
              lineHeight: 1.2
            }}
          >
            {getStatusDescription()}
          </Typography>
        }
      />
    </MenuItem>
  );
};

export default ConnectionStatusMenuItem; 